/**
 * Bounded acquisition for the Web Locks API.
 *
 * The raw zero-wait form — `navigator.locks.request(name, { ifAvailable: true }, cb)` —
 * hands the callback `null` the instant the lock is held by anyone else and never tries
 * again. Every use of it in this package turned a *transient* condition into a
 * *permanent* one, because the loser cached the miss as a terminal state with nothing
 * above it to surface the loss (storage writer handover on reload, leader-election grant
 * race, ephemeral-context OPFS). The bug was never the flake; it was that a recoverable
 * miss was recorded as unrecoverable.
 *
 * This helper is the single sanctioned home for `{ ifAvailable: true }`. It re-attempts
 * the acquisition at `retryIntervalMs` until `timeoutMs` elapses, and — critically —
 * reports exhaustion as a **typed, non-terminal** value (`LockUnavailableError`) rather
 * than a thrown terminal error. It is a *value*, never something the caller is nudged to
 * cache: `boundedAcquire` returns a discriminated union whose failure branch carries no
 * lock to leak, and `withBoundedLock` releases automatically, so a caller cannot hold a
 * lock past its work nor persist the miss as a dead end.
 *
 * `client/adapters-web` is the only layer allowed to touch `navigator.locks` (a browser
 * global), so the helper lives here alongside `BrowserClock` / `BrowserId`.
 */
/**
 * Non-terminal outcome of a bounded acquisition: the lock was held by someone else for
 * the entire window. Distinct from any persistent/terminal failure class on purpose — a
 * caller that maps this onto a cached dead-end state is reintroducing the very bug this
 * helper exists to prevent.
 */
export class LockUnavailableError extends Error {
    lockName;
    waitedMs;
    constructor(lockName, waitedMs) {
        super(`Web Lock "${lockName}" stayed held for ${waitedMs}ms; acquisition is retryable, not terminal`);
        this.name = "LockUnavailableError";
        this.lockName = lockName;
        this.waitedMs = waitedMs;
    }
}
const NOOP_RELEASE = async () => undefined;
function defaultDelay(milliseconds) {
    return new Promise((resolve) => { globalThis.setTimeout(resolve, milliseconds); });
}
function defaultElapsed() {
    return globalThis.performance.now();
}
/**
 * A single zero-wait attempt that, on success, keeps the lock held until the returned
 * `release` runs. The Web Locks API frees a lock when its callback settles, so holding it
 * open past the callback requires parking on an internal promise that `release` resolves.
 */
async function attemptHeld(locks, name, mode) {
    let resolveAttempt;
    let rejectAttempt;
    const settled = new Promise((resolve, reject) => {
        resolveAttempt = resolve;
        rejectAttempt = reject;
    });
    let releaseHold;
    const hold = new Promise((resolve) => { releaseHold = resolve; });
    const options = mode === undefined
        ? { ifAvailable: true }
        : { ifAvailable: true, mode };
    const request = locks.request(name, options, async (lock) => {
        if (lock === null) {
            resolveAttempt?.({ granted: false });
            return;
        }
        resolveAttempt?.({ granted: true });
        await hold;
    });
    void request.catch((error) => rejectAttempt?.(error));
    const outcome = await settled;
    if (!outcome.granted) {
        // The request callback has already returned, so the lock (never granted) needs no
        // teardown; await the request so a rejection still surfaces rather than dangling.
        return { acquired: false, release: NOOP_RELEASE };
    }
    let released = false;
    return {
        acquired: true,
        release: async () => {
            if (released)
                return;
            released = true;
            releaseHold?.();
            await request;
        },
    };
}
/**
 * Acquire `name` under an exclusive-by-caller Web Lock, retrying the zero-wait request
 * until `timeoutMs`. Resolves to a held lock (the caller MUST call `release`) or a typed,
 * non-terminal `LockUnavailableError`. Use this only when the lock must outlive a single
 * function call (e.g. a database writer lease held for the tab's lifetime); prefer
 * {@link withBoundedLock} otherwise, which cannot leak the lock.
 */
export async function boundedAcquire(locks, name, options) {
    const delay = options.delay ?? defaultDelay;
    const elapsed = options.elapsed ?? defaultElapsed;
    const start = elapsed();
    const deadline = start + options.timeoutMs;
    for (;;) {
        const attempt = await attemptHeld(locks, name, options.mode);
        if (attempt.acquired)
            return { acquired: true, release: attempt.release };
        const now = elapsed();
        if (now >= deadline) {
            return { acquired: false, error: new LockUnavailableError(name, Math.max(0, now - start)) };
        }
        await delay(options.retryIntervalMs);
    }
}
/**
 * Run `work` while holding `name`, releasing automatically when it settles — the safe
 * default. Returns the work's value on success, or a typed, non-terminal
 * `LockUnavailableError` if the lock stayed held for the whole window. The lock can never
 * be leaked because release is tied to `work` completing.
 */
export async function withBoundedLock(locks, name, options, work) {
    const outcome = await boundedAcquire(locks, name, options);
    if (!outcome.acquired)
        return { acquired: false, error: outcome.error };
    try {
        return { acquired: true, value: await work() };
    }
    finally {
        await outcome.release();
    }
}
//# sourceMappingURL=bounded-lock.js.map